// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the person's own minutes in "Time and client" (AgentTimePanel). Konvo cannot measure them,
// so it estimates them from what it knows (AgentTimeModel youTurnMinutes: prompts, their words,
// answers and approvals, and the changed lines to review). "You: 5 turns · ~14 min (estimate)": the
// estimate opens a small editor where the person sets their own minutes for the chat (then shown
// without "estimate") or goes back to the estimate; "How it's estimated" shows the sum for the chat
// and the knobs, which the person can tweak for every chat they have not set by hand.

import {agentErrorMessage, coarseDuration} from '@app/features/konvo_agents/components/AgentsCopy';
import styles from '@app/features/konvo_agents/components/AgentTimePanel.module.css';
import AgentTime from '@app/features/konvo_agents/state/AgentTime';
import {
	AGENT_ESTIMATE_DEFAULTS,
	type AgentEstimateKnobs,
	type AgentTimeView,
} from '@konvo/schema/src/domains/konvo/AgentTime';
import {wholeMinutes, youEstimateOfBasis} from '@konvo/schema/src/domains/konvo/AgentTimeModel';
import type {I18n} from '@lingui/core';
import {msg, plural} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {IconAdjustmentsHorizontal, IconPencil} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useEffect, useRef, useState} from 'react';

const MINUTES = msg({
	message: '{n} min',
	comment: 'Agents Time and client: a number of minutes of the person’s own time, e.g. "14 min".',
});

/** "14 min" under an hour and a half, else "2h 14m". */
export function minutesAmount(i18n: I18n, minutes: number): string {
	const whole = Math.max(0, Math.round(minutes));
	if (whole < 90) return i18n._(MINUTES, {n: whole});
	return coarseDuration(i18n, whole * 60);
}

/** A number typed into a field, or null when it is not one in [min, max]. */
function typed(value: string, min: number, max: number): number | null {
	const number = Number(value.replace(',', '.'));
	return value.trim() !== '' && Number.isFinite(number) && number >= min && number <= max ? number : null;
}

type Open = 'edit' | 'how' | null;

/** "~14 min (estimate)" or the person's "25 min", and the two small editors. */
export const YouMinutes = observer(
	({machineId, threadId, view}: {machineId: string; threadId: string; view: AgentTimeView}) => {
		const {i18n, t} = useLingui();
		const [open, setOpen] = useState<Open>(null);
		useEffect(() => setOpen(null), [machineId, threadId]);
		const you = view.you;
		const amount = minutesAmount(i18n, you.minutes);
		return (
			<>
				<button
					type="button"
					className={styles.youButton}
					onClick={() => setOpen(open === 'edit' ? null : 'edit')}
					aria-expanded={open === 'edit'}
					title={
						you.edited
							? t({
									message: 'Your minutes on this chat, as you set them. Click to change them.',
									comment: 'Agents Time and client: tooltip on the minutes the person set for this chat.',
								})
							: t({
									message: 'An estimate of your own minutes on this chat. Click to set your own.',
									comment: 'Agents Time and client: tooltip on the estimate of the person’s minutes.',
								})
					}
					data-flx="konvo-agents.time.you"
					data-edited={you.edited ? 'true' : undefined}
				>
					{you.edited ? amount : `~${amount}`}
					{!you.edited && ' '}
					{!you.edited && (
						<span className={styles.youEstimate}>
							{t({
								message: '(estimate)',
								comment: 'Agents Time and client: after the person’s estimated minutes, e.g. "~14 min (estimate)".',
							})}
						</span>
					)}
					<IconPencil size={13} stroke={1.8} className={styles.youPencil} />
				</button>
				<button
					type="button"
					className={styles.howButton}
					onClick={() => setOpen(open === 'how' ? null : 'how')}
					aria-expanded={open === 'how'}
					data-flx="konvo-agents.time.estimate-how"
				>
					<IconAdjustmentsHorizontal size={14} stroke={1.8} />
					{t({
						message: 'How it’s estimated',
						comment: 'Agents Time and client: opens how the person’s own minutes are estimated, with its settings.',
					})}
				</button>
				{open === 'edit' && (
					<YouEditor machineId={machineId} threadId={threadId} view={view} onClose={() => setOpen(null)} />
				)}
				{open === 'how' && (
					<EstimateKnobs machineId={machineId} threadId={threadId} view={view} onClose={() => setOpen(null)} />
				)}
			</>
		);
	},
);

/** Closes on Escape. */
function useEscape(onClose: () => void) {
	useEffect(() => {
		const key = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.stopPropagation();
				onClose();
			}
		};
		document.addEventListener('keydown', key, true);
		return () => document.removeEventListener('keydown', key, true);
	}, [onClose]);
}

function YouEditor({
	machineId,
	threadId,
	view,
	onClose,
}: {
	machineId: string;
	threadId: string;
	view: AgentTimeView;
	onClose: () => void;
}) {
	const {i18n, t} = useLingui();
	const you = view.you;
	const [value, setValue] = useState(String(you.minutes));
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const input = useRef<HTMLInputElement>(null);
	useEscape(onClose);
	useEffect(() => {
		input.current?.select();
	}, []);
	const minutes = typed(value, 0, 6000);
	const save = async (next: number | null) => {
		setBusy(true);
		setError(null);
		try {
			await AgentTime.setYou(machineId, threadId, next === null ? null : Math.round(next));
			onClose();
		} catch (failure) {
			setError(agentErrorMessage(i18n, failure));
		} finally {
			setBusy(false);
		}
	};
	const estimate = minutesAmount(i18n, you.estimateMinutes);
	return (
		<form
			className={styles.youPop}
			onSubmit={(event) => {
				event.preventDefault();
				if (minutes !== null) void save(minutes);
			}}
			data-flx="konvo-agents.time.you-editor"
		>
			<b className={styles.youPopTitle}>
				{t({
					message: 'Your minutes on this chat',
					comment: 'Agents Time and client: heading of the small editor where the person sets their own minutes.',
				})}
			</b>
			<div className={styles.youInputRow}>
				<label className={styles.numberField}>
					<input
						ref={input}
						type="number"
						inputMode="numeric"
						min={0}
						max={6000}
						step={1}
						value={value}
						onChange={(event) => setValue(event.target.value)}
						aria-label={t({
							message: 'Your minutes on this chat',
							comment: 'Agents Time and client: heading of the small editor where the person sets their own minutes.',
						})}
						data-flx="konvo-agents.time.you-input"
					/>
					<span>{t({message: 'min', comment: 'Agents Time and client: unit after a minutes field.'})}</span>
				</label>
				<button
					type="submit"
					className={styles.primary}
					disabled={busy || minutes === null}
					data-flx="konvo-agents.time.you-save"
				>
					{t`Save`}
				</button>
				<button type="button" className={styles.secondary} onClick={onClose} disabled={busy}>
					{t`Cancel`}
				</button>
			</div>
			<span className={styles.youPopNote}>
				{you.edited ? (
					<>
						{t({
							message: `The estimate is ~${estimate}.`,
							comment: 'Agents Time and client: what the estimate would be, e.g. "The estimate is ~14 min."',
						})}{' '}
						<button
							type="button"
							className={styles.textButton}
							onClick={() => void save(null)}
							disabled={busy}
							data-flx="konvo-agents.time.you-reset"
						>
							{t({
								message: 'Use the estimate',
								comment: 'Agents Time and client: drop the minutes the person set and go back to the estimate.',
							})}
						</button>
					</>
				) : (
					t({
						message: 'Set your own and it counts instead of the estimate, in the week and when you log time.',
						comment: 'Agents Time and client: under the minutes field, what setting your own minutes does.',
					})
				)}
			</span>
			{error && <span className={styles.error}>{error}</span>}
		</form>
	);
}

interface KnobRow {
	name: keyof AgentEstimateKnobs;
	label: string;
	unit: string;
	min: number;
	max: number;
	step: number;
	/** What this chat has of it, and the minutes it comes to. */
	detail: (knobs: AgentEstimateKnobs) => string;
}

function EstimateKnobs({
	machineId,
	threadId,
	view,
	onClose,
}: {
	machineId: string;
	threadId: string;
	view: AgentTimeView;
	onClose: () => void;
}) {
	const {i18n, t} = useLingui();
	const basis = view.you.basis;
	const [draft, setDraft] = useState<Record<keyof AgentEstimateKnobs, string>>(() => texts(view.you.knobs));
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	useEscape(onClose);
	const min = (minutes: number) => minutesAmount(i18n, minutes);
	const rows: Array<KnobRow> = [
		{
			name: 'promptMinutes',
			label: t({
				message: 'Per prompt',
				comment: 'Agents How it’s estimated: minutes for each prompt (reading the reply, deciding what next).',
			}),
			unit: t({message: 'min', comment: 'Agents Time and client: unit after a minutes field.'}),
			min: 0,
			max: 30,
			step: 0.5,
			detail: (knobs) => {
				const prompts = basis.prompts;
				const amount = min(prompts * knobs.promptMinutes);
				return plural(prompts, {one: `# prompt · ${amount}`, other: `# prompts · ${amount}`});
			},
		},
		{
			name: 'wordsPerMinute',
			label: t({
				message: 'Writing speed',
				comment: 'Agents How it’s estimated: how many words a minute the person writes prompts at.',
			}),
			unit: t({message: 'words/min', comment: 'Agents How it’s estimated: unit after the writing speed field.'}),
			min: 5,
			max: 200,
			step: 5,
			detail: (knobs) => {
				const words = basis.words;
				const amount = min(words / Math.max(1, knobs.wordsPerMinute));
				return plural(words, {one: `# word · ${amount}`, other: `# words · ${amount}`});
			},
		},
		{
			name: 'answerMinutes',
			label: t({
				message: 'Per answer or approval',
				comment: 'Agents How it’s estimated: minutes for each question answered or approval given.',
			}),
			unit: t({message: 'min', comment: 'Agents Time and client: unit after a minutes field.'}),
			min: 0,
			max: 30,
			step: 0.5,
			detail: (knobs) => {
				const answers = basis.answers + basis.approvals;
				const amount = min(answers * knobs.answerMinutes);
				return plural(answers, {one: `# answer · ${amount}`, other: `# answers · ${amount}`});
			},
		},
		{
			name: 'reviewMinutesPer100Lines',
			label: t({
				message: 'Review per 100 lines',
				comment: 'Agents How it’s estimated: minutes to review 100 lines the agent changed.',
			}),
			unit: t({message: 'min', comment: 'Agents Time and client: unit after a minutes field.'}),
			min: 0,
			max: 120,
			step: 1,
			detail: (knobs) => {
				const lines = basis.lines;
				const amount = min((lines / 100) * knobs.reviewMinutesPer100Lines);
				return plural(lines, {one: `# changed line · ${amount}`, other: `# changed lines · ${amount}`});
			},
		},
	];
	const parsed = Object.fromEntries(rows.map((row) => [row.name, typed(draft[row.name], row.min, row.max)])) as Record<
		keyof AgentEstimateKnobs,
		number | null
	>;
	const valid = rows.every((row) => parsed[row.name] !== null);
	const shown: AgentEstimateKnobs = valid ? (parsed as AgentEstimateKnobs) : view.you.knobs;
	const total = minutesAmount(i18n, wholeMinutes(youEstimateOfBasis(basis, shown)));
	const save = async (knobs: AgentEstimateKnobs | null) => {
		setBusy(true);
		setError(null);
		try {
			await AgentTime.setKnobs(knobs, {machineId, threadId});
			onClose();
		} catch (failure) {
			setError(agentErrorMessage(i18n, failure));
		} finally {
			setBusy(false);
		}
	};
	return (
		<form
			className={styles.youPop}
			onSubmit={(event) => {
				event.preventDefault();
				if (valid) void save(shown);
			}}
			data-flx="konvo-agents.time.estimate"
		>
			<b className={styles.youPopTitle}>
				{t({
					message: 'How your minutes are estimated',
					comment: 'Agents How it’s estimated: heading of the popover with the estimate’s sum and settings.',
				})}
			</b>
			<span className={styles.youPopNote}>
				{t({
					message:
						'Konvo can’t see your screen, so it adds up what you wrote, answered and had to review. Changes apply to every chat whose minutes you haven’t set.',
					comment: 'Agents How it’s estimated: what the estimate rests on and where the settings apply.',
				})}
			</span>
			<div className={styles.knobs}>
				{rows.map((row) => (
					<div key={row.name} className={styles.knob} data-flx={`konvo-agents.time.knob.${row.name}`}>
						<span className={styles.knobLabel}>{row.label}</span>
						<label className={clsx(styles.numberField, styles.knobField)}>
							<input
								type="number"
								inputMode="decimal"
								min={row.min}
								max={row.max}
								step={row.step}
								value={draft[row.name]}
								onChange={(event) => setDraft({...draft, [row.name]: event.target.value})}
								aria-label={row.label}
								aria-invalid={parsed[row.name] === null}
								data-flx={`konvo-agents.time.knob-input.${row.name}`}
							/>
							<span>{row.unit}</span>
						</label>
						<span className={styles.knobDetail}>{row.detail(shown)}</span>
					</div>
				))}
			</div>
			<div className={styles.knobTotal} data-flx="konvo-agents.time.estimate-total">
				<span>
					{t({
						message: 'This chat',
						comment: 'Agents How it’s estimated: label before the estimate for the open chat.',
					})}
				</span>
				<b>~{total}</b>
			</div>
			<div className={styles.confirmButtons}>
				<button
					type="submit"
					className={styles.primary}
					disabled={busy || !valid}
					data-flx="konvo-agents.time.estimate-save"
				>
					{t`Save`}
				</button>
				<button
					type="button"
					className={styles.secondary}
					onClick={() => {
						setDraft(texts(AGENT_ESTIMATE_DEFAULTS));
						void save(null);
					}}
					disabled={busy}
					data-flx="konvo-agents.time.estimate-defaults"
				>
					{t({
						message: 'Reset to defaults',
						comment: 'Agents How it’s estimated: put the estimate’s settings back to Konvo’s defaults.',
					})}
				</button>
			</div>
			{error && <span className={styles.error}>{error}</span>}
		</form>
	);
}

const texts = (knobs: AgentEstimateKnobs): Record<keyof AgentEstimateKnobs, string> => ({
	promptMinutes: String(knobs.promptMinutes),
	wordsPerMinute: String(knobs.wordsPerMinute),
	answerMinutes: String(knobs.answerMinutes),
	reviewMinutesPer100Lines: String(knobs.reviewMinutesPer100Lines),
});
